import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';
import { useTranslation } from 'react-i18next';
import {
  FC,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { Form, FormApi } from 'informed';
import {
  AuthContext,
  LoginForm as LoginFormInterface,
  ConfirmLogin as ConfirmLoginInterface,
  ConfirmLogin,
} from '@repo-level/core-front';

import { useFranceLocale } from 'hooks/franceLocale';
import { Button } from 'components/buttons';
import { ModalDefaultProps } from 'components/Modal';
import { FormGroup, FormGroupBottom } from 'components/form/groups/FormGroup';
import { Input } from 'components/form/Input';
import { required, validateEmail } from 'helpers/validators';
import { mailMask } from 'helpers/masks';
import { Password } from 'components/form/Password';
import { ModalsContext, ModalType } from 'contexts/modal';
import {
  ErrorGroup,
  ErrorGroupList,
  ErrorServe,
} from 'components/form/groups/ErrorGroup';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { isDateRange } from 'helpers/isDateRange';
import { ModalTemplate } from 'components/ModalTemplate';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { Logo } from 'components/Logo';
import { HaveAccountButton } from 'components/HaveAccountButton';
import { addUrlParameter, localStorageEmail } from 'helpers/common';
import { useLogin } from 'hooks/login';
import { ScFormSubmitGroup } from 'components/form/groups/styled';
import { AUTH_RATE_LIMIT_LOGIN_ATTEMPTS } from 'helpers/constants';
import { useNavigation } from 'react-navi';

enum Requirements {
  GA = 'one_time_password',
}

const EASTER_WONDERLAND_DATE_START = 'March 30, 2023 00:00:00';
const EASTER_WONDERLAND_DATE_END = 'April 17, 2023 23:59:59';

type LoginFormProps = ModalDefaultProps & {
  redirect?: string;
};

export const Login = forwardRef<HTMLDivElement, LoginFormProps>(
  ({ onClose, redirect }, ref) => {
    const { t } = useTranslation(['glossary', 'common']);

    const { isFRWithoutStag } = useFranceLocale();

    const { handleModal } = useContext(ModalsContext);

    const { isMobileS } = useContext(CommonUtilsContext);

    const modalSettings: ModalTemplateSettings = useMemo(() => {
      return isMobileS
        ? { type: 'mobile', mode: { type: 'default' } }
        : {
            type: 'desktop',
            size: 'small',
            mode: { type: 'default' },
          };
    }, [isMobileS]);

    return (
      <ModalTemplate
        ref={ref}
        onClose={onClose}
        settings={modalSettings}
        title={<Logo height={28} />}
        description={t('slogan')}
        footer={
          !isFRWithoutStag && (
            <HaveAccountButton
              descr={t('common:auth.login.registerDescription')}
              btnText={t('register')}
              handleClick={handleModal(ModalType.Registration)}
            />
          )
        }
      >
        <LoginForm onClose={onClose} redirect={redirect} />
      </ModalTemplate>
    );
  }
);

const LoginForm: FC<LoginFormProps> = ({ onClose, redirect }) => {
  const { t } = useTranslation('common');

  const { insertUser, pendingUser, pendingWallets } = useContext(AuthContext);
  const { handleModal } = useContext(ModalsContext);
  const { navigate } = useNavigation();

  const {
    handleLogin,
    userData,
    loginError,
    loginPending,
    requestCount,
  } = useLogin();

  const handleSubmit = useCallback(
    async (values: LoginFormInterface | ConfirmLoginInterface) => {
      await handleLogin(values);
    },
    [handleLogin]
  );

  useEffect(() => {
    (async () => {
      if (userData == null || userData.id == null) return;
      await insertUser(userData, REASON_FOR_UPDATING_USER.LOGIN);

      onClose && onClose();
      redirect && navigate(redirect);

      if (
        isDateRange(EASTER_WONDERLAND_DATE_START, EASTER_WONDERLAND_DATE_END)
      ) {
        handleModal(ModalType.EasterWonderland)();
      }
    })();
  }, [userData, insertUser, onClose, handleModal, redirect, navigate]);

  const requiredForm = useMemo(() => {
    if (loginError == null) return undefined;

    let _required: string | undefined = undefined;

    if (loginError.required != null && loginError.required.length !== 0) {
      _required = loginError.required[0];
    }

    return _required;
  }, [loginError]);

  const errorForm = useMemo(() => {
    if (loginError == null || loginError.errors == null) return [];

    let accError: { field: string; message: string }[] = [];

    if (loginError != null) {
      const l = Object.keys(loginError.errors).map((key) => ({
        field: key,
        message: Object.values(loginError.errors[key]),
      }));

      accError = [...accError, ...l] as { field: string; message: string }[];

      if (
        AUTH_RATE_LIMIT_LOGIN_ATTEMPTS != null &&
        requestCount >= AUTH_RATE_LIMIT_LOGIN_ATTEMPTS
      ) {
        const lockedAccountError = {
          field: 'account',
          message: `${t('auth.login.error.lockedAccount')}`,
        };

        accError = [...accError, lockedAccountError];
      }
    }

    return accError;
  }, [loginError, t, requestCount]);

  const form = useMemo(() => {
    const formProps = {
      onSubmit: handleSubmit,
      onRecoveryModal: handleModal(ModalType.Recovery),
      pending: loginPending || pendingUser || pendingWallets,
      error: errorForm,
    };

    switch (requiredForm) {
      case Requirements.GA:
        return <TwoFAForm {...formProps} />;
      default:
        return <DefaultForm {...formProps} />;
    }
  }, [
    requiredForm,
    handleSubmit,
    loginPending,
    pendingUser,
    pendingWallets,
    errorForm,
    handleModal,
  ]);

  return <>{form}</>;
};

interface BaseFormInterface {
  onSubmit: (values: LoginFormInterface | ConfirmLoginInterface) => void;
  onRecoveryModal: () => void;
  pending: boolean;
  error: ErrorServe[];
}

const DefaultForm: FC<BaseFormInterface> = ({
  onSubmit,
  onRecoveryModal,
  pending,
  error,
}) => {
  const { t } = useTranslation();
  const formApiRef = useRef<FormApi<LoginFormInterface>>();

  const lockedAccountError = useMemo(
    () => error.find((e) => e.field === 'account'),
    [error]
  );

  const handleOpenRecovery = () => {
    const email = formApiRef.current?.getValue<string>('email');

    if (email) addUrlParameter('email', email);
    onRecoveryModal();
  };

  return (
    <Form<LoginFormInterface>
      getApi={(api) => {
        formApiRef.current = api;
      }}
      onSubmit={onSubmit}
    >
      <FormGroup>
        <Input
          autoComplete="off"
          animatedPlaceholder={t('glossary:email')}
          validateOnBlur
          field="email"
          validate={validateEmail}
          mask={mailMask}
          initialValue={`${localStorageEmail.get()}`}
        />
      </FormGroup>
      <FormGroup>
        <Password
          autoComplete="off"
          animatedPlaceholder={t('glossary:password')}
          field="password"
          validate={required}
          validateOnBlur
        />
      </FormGroup>

      {error !== null && error.length !== 0 && (
        <ErrorGroup>
          {lockedAccountError
            ? lockedAccountError.message
            : `${t('auth.login.invalidCredentials')}`}
        </ErrorGroup>
      )}

      <ScFormSubmitGroup>
        <Button green xLarge wide pending={pending} type="submit">
          {t('glossary:login')}
        </Button>
      </ScFormSubmitGroup>
      <FormGroupBottom>
        <button className="button-default" onClick={handleOpenRecovery}>
          {t('common:auth.login.forgotPassword')}
        </button>
      </FormGroupBottom>
    </Form>
  );
};

export const TwoFAForm: FC<BaseFormInterface> = ({
  onSubmit,
  onRecoveryModal,
  pending,
  error,
}) => {
  const { t } = useTranslation();

  return (
    <Form<ConfirmLogin> onSubmit={onSubmit}>
      <FormGroup>
        <Input
          autoComplete="off"
          animatedPlaceholder="Enter code"
          validateOnBlur
          field="otp_attempt"
          validate={required}
        />
      </FormGroup>
      {error != null && error.length !== 0 && <ErrorGroupList errors={error} />}

      <Button type="submit" green xLarge wide pending={pending}>
        {t('glossary:login')}
      </Button>
      <FormGroupBottom>
        <button className="button-default" onClick={onRecoveryModal}>
          {t('common:auth.login.forgotPassword')}
        </button>
      </FormGroupBottom>
    </Form>
  );
};
